// User security screen template

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../Constants";
import NavHeightAdjust from "../navbar/NavHeightAdjust";
import { useAuth } from "../../contexts/AuthContext";

const Modal = ({ isOpen, onClose, editor, user }) => {
  useEffect(() => {
    setUserRole(user.role);
    setModuleAccess({
      callCenter: user.callCenter.split("") || ["0", "0", "0", "0"],
      maintenanceContracts: user.maintenanceContracts.split("") || [
        "0",
        "0",
        "0",
        "0",
      ],
      inspectionsViolations: user.inspectionsViolations.split("") || [
        "0",
        "0",
        "0",
        "0",
      ],
      invoicing: user.invoicing.split("") || ["0", "0", "0", "0"],
      proposalsJobs: user.proposalsJobs.split("") || ["0", "0", "0", "0"],
      reports: user.reports.split("") || ["0", "0", "0", "0"],
      setup: user.setup.split("") || ["0", "0", "0", "0"],
    });
  }, []);

  const [userRole, setUserRole] = useState(user.role);
  const [moduleAccess, setModuleAccess] = useState({
    callCenter: user.callCenter.split(""),
    maintenanceContracts: user.maintenanceContracts.split(""),
    inspectionsViolations: user.inspectionsViolations.split(""),
    invoicing: user.invoicing.split(""),
    proposalsJobs: user.proposalsJobs.split(""),
    reports: user.reports.split(""),
    setup: user.setup.split(""),
  });

  if (!isOpen) return null;

  const handleRoleChange = (event) => {
    setUserRole(event.target.value);
    if (["ADMIN", "POWER_USER"].includes(event.target.value)) {
      setModuleAccess({
        callCenter: ["1", "1", "1", "1"],
        maintenanceContracts: ["1", "1", "1", "1"],
        inspectionsViolations: ["1", "1", "1", "1"],
        invoicing: ["1", "1", "1", "1"],
        proposalsJobs: ["1", "1", "1", "1"],
        reports: ["1", "1", "1", "1"],
        setup: ["1", "1", "1", "1"],
      });
    } else if (event.target.value === "MANAGER") {
      setModuleAccess({
        callCenter: ["0", "0", "0", "0"],
        maintenanceContracts: ["0", "0", "0", "0"],
        inspectionsViolations: ["0", "0", "0", "0"],
        invoicing: ["0", "0", "0", "0"],
        proposalsJobs: ["0", "0", "0", "0"],
        reports: ["0", "0", "0", "0"],
        setup: ["0", "0", "0", "0"],
      });
    } else {
      setModuleAccess({
        callCenter: ["1", "0", "0", "0"],
        maintenanceContracts: ["0", "0", "0", "0"],
        inspectionsViolations: ["0", "0", "0", "0"],
        invoicing: ["0", "0", "0", "0"],
        proposalsJobs: ["0", "0", "0", "0"],
        reports: ["0", "0", "0", "0"],
        setup: ["0", "0", "0", "0"],
      });
    }
  };

  const handleChange = (module, index, value) => {
    const updatedModuleAccess = { ...moduleAccess };
    updatedModuleAccess[module][index] = value;
    setModuleAccess(updatedModuleAccess);
  };

  const handleSave = async () => {
    const updatedUser = {
      ...user,
      role: userRole,
      callCenter: moduleAccess.callCenter.join(""),
      maintenanceContracts: moduleAccess.maintenanceContracts.join(""),
      inspectionsViolations: moduleAccess.inspectionsViolations.join(""),
      invoicing: moduleAccess.invoicing.join(""),
      proposalsJobs: moduleAccess.proposalsJobs.join(""),
      reports: moduleAccess.reports.join(""),
      setup: moduleAccess.setup.join(""),
    };
    console.log("Updated User: ", updatedUser);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/update_security`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updatedUser),
        }
      );

      let data = await response.text();
      console.log("test", data);
    } catch (error) {
      console.error("Error: ", error);
    }

    onClose();
  };

  const valueMap = {
    1: "ALLOWED",
    0: "FORBIDDEN",
  };

  const renderModuleRow = (moduleName, moduleKey) => (
    <tr key={moduleKey}>
      <td className="border px-4 py-2">{moduleName}</td>
      {["VIEW", "CREATE", "UPDATE", "DELETE"].map((action, index) => (
        <td className="border px-4 py-2" key={index}>
          <select
            name={`${moduleKey}${action}`}
            value={moduleAccess[moduleKey][index]}
            onChange={(e) => handleChange(moduleKey, index, e.target.value)}
            className="border w-full px-3 py-2 rounded mt-1"
            disabled={userRole === "POWER_USER"}
          >
            <option value="1">ALLOWED</option>
            <option value="0">FORBIDDEN</option>
          </select>
        </td>
      ))}
    </tr>
  );

  const modules = [
    { name: "Call Center", key: "callCenter" },
    { name: "Maintenance Contracts", key: "maintenanceContracts" },
    { name: "Inspections & Violations", key: "inspectionsViolations" },
    { name: "Invoicing", key: "invoicing" },
    { name: "Proposals & Jobs", key: "proposalsJobs" },
    { name: "Reports", key: "reports" },
    { name: "Setup", key: "setup" },
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white p-6 rounded shadow-lg w-3/5">
        <h2 className="text-xl font-bold mb-4">
          Edit Security for {user.username}
        </h2>

        <label className="block mb-2">
          Username:
          <input
            type="text"
            value={user.username}
            className="border w-full px-3 py-2 rounded mt-1"
            readOnly
          />
        </label>

        <label className="block mb-2">
          Role:
          <select
            name="role"
            value={userRole}
            onChange={handleRoleChange}
            className="border w-full px-3 py-2 rounded mt-1"
            disabled={editor.role != "POWER_USER"}
          >
            <option value="POWER_USER">POWER_USER</option>
            <option value="ADMIN">ADMIN</option>
            <option value="MANAGER">MANAGER</option>
            <option value="DISPATCHER">DISPATCHER</option>
          </select>
        </label>

        <table className="table-auto">
          <thead>
            <tr>
              <th className="px-4 py-2">Module</th>
              <th className="px-4 py-2">VIEW</th>
              <th className="px-4 py-2">CREATE</th>
              <th className="px-4 py-2">UPDATE</th>
              <th className="px-4 py-2">DELETE</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((module) => renderModuleRow(module.name, module.key))}
          </tbody>
        </table>

        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const UserSecurity = () => {
  const {  user } = useAuth();
  const [userAccounts, setUserAccounts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (user.role !== "ADMIN" && user.role !== "POWER_USER") {
      nav("/dashboard");
    }
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setUserAccounts(data);
      } catch (error) {
        console.error("Fetch error: ", error);
      }
    };

    fetchUsers();
  }, []);

  const handleEditClick = (userAccount) => {
    setSelectedUser(userAccount);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/deleteUser/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setUserAccounts(
          userAccounts.filter((userAccount) => userAccount.id !== id)
        );
        alert("User deleted successfully");
      } else if (response.status === 401) {
        alert("Unauthorized. You cannot delete this user");
      } else {
        throw new Error("Failed to delete employee");
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      alert("An error occurred while deleting employee");
    }
  };

  const accessType = (access) => {
    if (!access) return null;
    access = access.split("");
    return (
      <ul>
        {access[0] === "1" ? <li> View </li> : null}
        {access[1] === "1" ? <li> Create </li> : null}
        {access[2] === "1" ? <li> Update </li> : null}
        {access[3] === "1" ? <li> Delete </li> : null}
      </ul>
    );
  };

  return (
    <NavHeightAdjust>
      <div className="flex flex-col items-center justify-center h-full">
        <table className="table-auto">
          <thead>
            <tr>
              <th className="px-4 py-2">Username</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Call Center</th>
              <th className="px-4 py-2">Maintenance Contracts</th>
              <th className="px-4 py-2">Inspections & Violations</th>
              <th className="px-4 py-2">Invoicing</th>
              <th className="px-4 py-2">Proposals & Jobs</th>
              <th className="px-4 py-2">Reports</th>
              <th className="px-4 py-2">Setup</th>

              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {userAccounts.map((userAccount, index) => (
              <tr key={index}>
                <td className="border px-4 py-2">{userAccount.username}</td>
                <td className="border px-4 py-2">{userAccount.role}</td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.callCenter)}
                </td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.maintenanceContracts)}{" "}
                </td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.inspectionsViolations)}
                </td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.invoicing)}
                </td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.proposalsJobs)}
                </td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.reports)}
                </td>
                <td className="border px-4 py-2">
                  {accessType(userAccount.setup)}
                </td>

                <td className="border px-4 py-2">
                  <button
                    onClick={() => handleEditClick(userAccount)}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full mb-3"
                  >
                    Edit Security
                  </button>
                  <button
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-full"
                    onClick={() => handleDelete(userAccount.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {selectedUser ? (
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            editor={user}
            user={selectedUser}
          />
        ) : null}
      </div>
    </NavHeightAdjust>
  );
};

export default UserSecurity;
